import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ArrowRightIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useSamouczek } from '../../context/SamouczekContext'
import { ikonaKroku } from './ikonyKrokow'
import { czasKroku, czasRazem } from '../../lib/samouczek/kroki'

/**
 * Okno powitania samouczka „Pierwsze kroki”: raz, po pierwszym wejściu nowego konta na pulpit albo stronę
 * główną. Konto firmowe widzi listę kroków z czasem i „Zaczynamy” albo „Pomiń samouczek”. Konto bez wybranego
 * typu dostaje jedno pytanie, czy prowadzi firmę: „Tak” prowadzi od razu do NIP w ustawieniach konta.
 * Krzyżyk i Escape odkładają okno do następnej wizyty (bez decyzji).
 */
const OknoPowitania = () => {
  const s = useSamouczek()
  const glowny = useRef(null)
  const { powitanie, bezTypu, stan, imie, nazwaFirmy, rozpocznij, pominCalosc, pozniej } = s

  useEffect(() => {
    if (!powitanie) return undefined
    glowny.current?.focus()
    const escape = (e) => { if (e.key === 'Escape') pozniej() }
    document.addEventListener('keydown', escape)
    return () => document.removeEventListener('keydown', escape)
  }, [powitanie, pozniej])

  if (!powitanie) return null

  const zostalo = stan.kroki.filter((k) => k.stan !== 'zrobiony').length
  const pracownik = s.fakty?.rola === 'member'

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center sm:p-6 print:hidden">
      <div role="dialog" aria-modal="true" aria-labelledby="samouczek-powitanie"
           className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl sm:p-7">
        <button type="button" onClick={pozniej} aria-label="Zamknij, przypomnij później"
                className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-xl text-gray-400 hover:bg-gray-100 hover:text-gray-700">
          <XMarkIcon className="h-5 w-5" />
        </button>

        <h2 id="samouczek-powitanie" className="pr-10 text-2xl font-bold tracking-tight text-gray-900">
          Witaj w VoltValue{imie ? `, ${imie}` : ''}!
        </h2>

        {bezTypu ? (
          <>
            <p className="mt-3 text-base font-semibold text-gray-900">Prowadzisz firmę fotowoltaiczną?</p>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">
              Skonfigurujemy razem konto firmy: pulpit, klienci, kalendarz montaży i zespół. Zajmie to kilka minut,
              a każdy krok możesz pominąć.
            </p>
            <div className="mt-6 flex flex-col gap-2.5 sm:flex-row">
              <button ref={glowny} type="button" onClick={() => rozpocznij({ odRazu: true })}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700">
                Tak, prowadzę firmę <ArrowRightIcon className="h-4 w-4" />
              </button>
              <button type="button" onClick={pominCalosc}
                      className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 hover:border-gray-300 hover:bg-gray-50">
                Nie, szukam instalacji dla siebie
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">
              {pracownik ? 'Skonfigurujmy Twoje konto' : 'Skonfigurujmy konto'}
              {nazwaFirmy ? <>{pracownik ? ' w firmie ' : ' firmy '}<span className="font-semibold text-gray-900">{nazwaFirmy}</span></> : ''}
              {/* Bez drugiej kropki po „sp. z o.o.” */}
              {nazwaFirmy.endsWith('.') ? '' : '.'}
              {' '}To {zostalo} {zostalo === 1 ? 'krótki krok' : zostalo < 5 ? 'krótkie kroki' : 'krótkich kroków'}, około {czasRazem(stan.minut)},
              a każdy możesz pominąć.
            </p>

            <ol className="mt-5 space-y-1">
              {stan.kroki.map(({ krok, stan: stanKroku }, i) => {
                const Ikona = ikonaKroku(krok.klucz)
                const zrobiony = stanKroku === 'zrobiony'
                return (
                  <li key={krok.klucz} className="flex items-center gap-3 rounded-xl px-2 py-2">
                    <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${zrobiony ? 'bg-emerald-600 text-white' : 'bg-blue-50 text-blue-600'}`}>
                      {zrobiony ? <CheckIcon className="h-5 w-5" strokeWidth={2.4} /> : <Ikona className="h-5 w-5" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-gray-900">{i + 1}. {krok.tytul}</span>
                      <span className="block truncate text-xs text-gray-500">{krok.krotko}</span>
                    </span>
                    <span className="flex-shrink-0 text-xs tabular-nums text-gray-400">{zrobiony ? 'gotowe' : czasKroku(krok.minut)}</span>
                  </li>
                )
              })}
            </ol>

            <div className="mt-6 flex flex-col gap-2.5 sm:flex-row-reverse">
              <button ref={glowny} type="button" onClick={() => rozpocznij()}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700">
                Zaczynamy <ArrowRightIcon className="h-4 w-4" />
              </button>
              <button type="button" onClick={pominCalosc}
                      className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 hover:border-gray-300 hover:bg-gray-50">
                Pomiń samouczek
              </button>
            </div>
            <p className="mt-3 text-center text-xs text-gray-500">Samouczek znajdziesz zawsze w menu konta, w pozycji Pierwsze kroki.</p>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}

export default OknoPowitania
